import {
  AggregateServicingProvider,
  ProviderHubContractEntities,
  MaxProviderMetric,
  ProviderHubFiltersRequestModel,
  ProviderHubServicingProviders,
  ServicingAccessPoint,
  AggregateContractEntity,
  ProviderHubContractEntityProviders,
  ProviderHubContractEntityCcnGroup,
  ProviderHubContractEntityFiltersRequestModel,
  ContractEntityProvidersFiltersRequestModel,
  ProviderHubContractEntityOrganizations,
  AggregateOrganization,
  AggregateHospital,
  HospitalService,
  MaxContractEntityMetric,
  ProviderHubOrganizations,
  ProviderHubOrganizationsFiltersRequestModel,
  ProviderHubHospitals,
  ProviderHubHospitalsFiltersRequestModel,
  MarketProviderJobStatusResponse,
  ProviderCountPerClaimsVolumePerLOB,
  GapImpactPaginationResult,
  AffiliationProfileAvailability,
  EffectivenessEfficiencyBreakdown,
  ReadyForReviewModel,
  Procedure,
  Condition
} from "@app/projects/network-assessment/provider-hub/interfaces";
import { PaginationResult } from "@app/shared/waffles/exports";
import { AccessPointDetails } from "@app/projects/network-assessment/provider-hub/provider-profile/interfaces/accessPointDetails";
import { AccessGapTypeEnum } from "@app/projects/network-assessment/provider-hub/enumerators";
import { ProviderServicingAccessPoint } from "@app/projects/network-assessment/provider-hub/provider-profile/interfaces/providerServicingAccessPoint";
import ServiceBase from "@app/shared/serviceBase";

export default class ProviderHubService extends ServiceBase {
  private readonly endpoint = "ServicingAccessPoints";

  /**
   * Get the providers that can impact any of the county/specialties in the providers table
   */
  public async getProviderHubServicingProviders(
    pageIndex: number,
    pageSize: number,
    filters: ProviderHubFiltersRequestModel
  ): Promise<GapImpactPaginationResult<ProviderHubServicingProviders>> {
    const data = await this.makePost<GapImpactPaginationResult<ProviderHubServicingProviders>>(`${this.endpoint}/servicing-providers`, {
      ...filters,
      pageIndex: --pageIndex,
      pageSize,
    });

    return data;
  }

  /**
   * Get the contract entities that can impact any of the county/specialties in the gap table. So far will display mocked data
   */
  public async getProviderHubContractEntities(
    pageIndex: number,
    pageSize: number,
    filters: ProviderHubContractEntityFiltersRequestModel
  ): Promise<GapImpactPaginationResult<ProviderHubContractEntities>> {
    const data = await this.makePost<GapImpactPaginationResult<ProviderHubContractEntities>>(`${this.endpoint}/contract-entity`, {
      ...filters,
      pageIndex: --pageIndex,
      pageSize,
    });

    return data;
  }

  /**
   * @param projectId integer representing the custom project id
   * @param projectRunConfigurationId integer representing the project run configuration id
   * @param servicingProviderKey string representing the servicing provider key
   * @returns an AggregateServicingProviderModel object containing the details of the servicing provider retrieved from the given custom project, project run configuration id and servicing provider key.
   */
  public async getServicingProviderDetails(projectId: number, projectRunConfigurationId: number, servicingProviderKey: string): Promise<AggregateServicingProvider> {
    return await this.makeGet<AggregateServicingProvider>(`${this.endpoint}/projects/${projectId}/run-configurations/${projectRunConfigurationId}/servicing-provider/${servicingProviderKey}`);
  }

  /**
   * @param projectId integer representing the custom project id
   * @param projectRunConfigurationId integer representing the project run configuration id
   * @param contractEntityId string representing the contract entity id
   * @returns an AggregateServicingProviderModel object containing the details of the servicing provider retrieved from the given custom project, project run configuration id and servicing provider key.
   */
  public async getContractEntityDetails(projectId: number, projectRunConfigurationId: number, contractEntityId: number): Promise<AggregateContractEntity> {
    return await this.makeGet<AggregateContractEntity>(`${this.endpoint}/projects/${projectId}/run-configurations/${projectRunConfigurationId}/contract-entity/${contractEntityId}`);
  }

  /**
  * @param projectId integer representing the custom project id
  * @param projectRunConfigurationId integer representing the project run configuration id
  * @param organizationNpi string representing the organization NPI
  * @param contractEntityId integer representing the contract entity id
  * @returns an AggregateServicingProviderModel object containing the details of the servicing provider retrieved from the given custom project, project run configuration id and servicing provider key.
*/
  public async getOrganizationDetails(projectId: number, projectRunConfigurationId: number, organizationNpi: string, contractEntityId: number): Promise<AggregateOrganization> {
    return await this.makeGet<AggregateOrganization>(`${this.endpoint}/projects/${projectId}/run-configurations/${projectRunConfigurationId}/organization/${organizationNpi}/contractEntityId/${contractEntityId}`);
  }

  /**
   * Returns which of the supplied contract entities / organizations have a Provider Hub profile
   * (i.e. at least one provider with a primary affiliation) for the given project run configuration.
   * Used by the provider profile affiliation accordion to decide whether a name should hyperlink -
   * entities with no profile would otherwise route to a page that spins indefinitely.
   */
  public async getAffiliationProfileAvailability(
    projectId: number,
    projectRunConfigurationId: number,
    contractEntityIds: number[],
    organizationNpis: string[],
    signal?: AbortSignal
  ): Promise<AffiliationProfileAvailability> {
    return await this.makePost<AffiliationProfileAvailability>(
      `${this.endpoint}/projects/${projectId}/run-configurations/${projectRunConfigurationId}/affiliation-profile-availability`,
      { contractEntityIds, organizationNpis },
      undefined,
      false,
      "application/json",
      true,
      undefined,
      signal
    );
  }

  public async getHospitalDetails(projectId: number, projectRunConfigurationId: number, ccn: string): Promise<AggregateHospital> {
    return await this.makeGet<AggregateHospital>(`${this.endpoint}/projects/${projectId}/run-configurations/${projectRunConfigurationId}/hospital/${ccn}`);
  }

  public async getHospitalProvidersDetails(
    pageIndex: number,
    pageSize: number,
    projectId: number,
    projectRunConfigurationId: number,
    ccn: string,
    filters: ContractEntityProvidersFiltersRequestModel,
    signal?: AbortSignal
  ): Promise<GapImpactPaginationResult<ProviderHubContractEntityProviders>> {
    const data = await this.makePost<GapImpactPaginationResult<ProviderHubContractEntityProviders>>(`${this.endpoint}/projects/${projectId}/run-configurations/${projectRunConfigurationId}/hospital-providers/${ccn}`, {
      ...filters,
      pageIndex: --pageIndex,
      pageSize
    }, undefined, false, "application/json", true, undefined, signal);

    return data;
  }

  // "Services" = NPI type-2 organizations affiliated with the hospital.
  public async getHospitalServices(
    pageIndex: number,
    pageSize: number,
    projectId: number,
    projectRunConfigurationId: number,
    ccn: string,
    filters: ContractEntityProvidersFiltersRequestModel,
    signal?: AbortSignal
  ): Promise<GapImpactPaginationResult<HospitalService>> {
    const data = await this.makePost<GapImpactPaginationResult<HospitalService>>(`${this.endpoint}/projects/${projectId}/run-configurations/${projectRunConfigurationId}/hospital-services/${ccn}`, {
      ...filters,
      pageIndex: --pageIndex,
      pageSize
    }, undefined, false, "application/json", true, undefined, signal);

    return data;
  }

  public async getContractEntityProviderDetails(
    pageIndex: number,
    pageSize: number,
    projectId: number,
    projectRunConfigurationId: number,
    contractEntityId: number,
    filters: ContractEntityProvidersFiltersRequestModel
  ): Promise<PaginationResult<ProviderHubContractEntityProviders>> {
    const data = await this.makePost<PaginationResult<ProviderHubContractEntityProviders>>(`${this.endpoint}/projects/${projectId}/run-configurations/${projectRunConfigurationId}/contract-entity-providers/${contractEntityId}`, {
      ...filters,
      pageIndex: --pageIndex,
      pageSize,
    }
    );

    return data;
  }

  public async getContractEntityOrganizationDetails(
    pageIndex: number,
    pageSize: number,
    projectId: number,
    projectRunConfigurationId: number,
    contractEntityId: number,
    filters: ContractEntityProvidersFiltersRequestModel
  ): Promise<PaginationResult<ProviderHubContractEntityOrganizations>> {
    const data = await this.makePost<PaginationResult<ProviderHubContractEntityOrganizations>>(`${this.endpoint}/projects/${projectId}/run-configurations/${projectRunConfigurationId}/contract-entity-organizations/${contractEntityId}`, {
      ...filters,
      pageIndex: --pageIndex,
      pageSize
    });

    return data;
  }

  // type-1 practitioners of the CE (client-grouped by organization in the Employed Practitioners tab).
  public async getContractEntityEmployedPractitioners(
    pageIndex: number,
    pageSize: number,
    projectId: number,
    projectRunConfigurationId: number,
    contractEntityId: number,
    filters: ContractEntityProvidersFiltersRequestModel
  ): Promise<GapImpactPaginationResult<ProviderHubContractEntityProviders>> {
    const data = await this.makePost<GapImpactPaginationResult<ProviderHubContractEntityProviders>>(`${this.endpoint}/projects/${projectId}/run-configurations/${projectRunConfigurationId}/contract-entity-employed-practitioners/${contractEntityId}`, {
      ...filters,
      pageIndex: --pageIndex,
      pageSize,
    });

    return data;
  }

  // type-2 facilities of the CE (client-grouped by CCN in the Facilities tab; rows carry ccn + hospitalName).
  public async getContractEntityFacilities(
    pageIndex: number,
    pageSize: number,
    projectId: number,
    projectRunConfigurationId: number,
    contractEntityId: number,
    filters: ContractEntityProvidersFiltersRequestModel
  ): Promise<GapImpactPaginationResult<ProviderHubContractEntityProviders>> {
    const data = await this.makePost<GapImpactPaginationResult<ProviderHubContractEntityProviders>>(`${this.endpoint}/projects/${projectId}/run-configurations/${projectRunConfigurationId}/contract-entity-facilities/${contractEntityId}`, {
      ...filters,
      pageIndex: --pageIndex,
      pageSize,
    });

    return data;
  }

  // (Option A parent-pagination): paginated CCN (hospital) parent rows for the CE Facilities tab "View by: CCN".
  // Children (the individual facilities under a CCN) are lazy-loaded via getContractEntityFacilities with filters.ccn set
  // (or filters.otherFacilitiesOnly for the "Other Facilities" group).
  public async getContractEntityFacilityCcnGroups(
    pageIndex: number,
    pageSize: number,
    projectId: number,
    projectRunConfigurationId: number,
    contractEntityId: number,
    filters: ContractEntityProvidersFiltersRequestModel
  ): Promise<GapImpactPaginationResult<ProviderHubContractEntityCcnGroup>> {
    const data = await this.makePost<GapImpactPaginationResult<ProviderHubContractEntityCcnGroup>>(`${this.endpoint}/projects/${projectId}/run-configurations/${projectRunConfigurationId}/contract-entity-facility-ccn-groups/${contractEntityId}`, {
      ...filters,
      pageIndex: --pageIndex,
      pageSize,
    });

    return data;
  }

  public async getOrganizationProvidersDetails(
    pageIndex: number,
    pageSize: number,
    projectId: number,
    projectRunConfigurationId: number,
    organizationId: string,
    filters: ContractEntityProvidersFiltersRequestModel
  ): Promise<GapImpactPaginationResult<ProviderHubContractEntityProviders>> {
    const data = await this.makePost<GapImpactPaginationResult<ProviderHubContractEntityProviders>>(`${this.endpoint}/projects/${projectId}/run-configurations/${projectRunConfigurationId}/organization-providers/${organizationId}`, {
      ...filters,
      pageIndex: --pageIndex,
      pageSize
    });

    return data;
  }

  /**
   * Get the access points associated to the given servicing provider ID
   *  @param projectId integer representing the custom project id
   * @param projectRunConfigurationId integer representing the project run configuration id
   * @param servicingProviderKey string representing the servicing provider key
   */
  public async getAccessPointsByServicingProviders(projectId: number, projectRunConfigurationId: number, servicingProviderKey: string): Promise<ServicingAccessPoint[]> {
    return await this.makeGet<ServicingAccessPoint[]>(`${this.endpoint}/projects/${projectId}/run-configurations/${projectRunConfigurationId}/servicing-provider/${servicingProviderKey}/access-points`);
  }

  /**
 * Get the access points associated to the given contract entity ID
 *  @param projectId integer representing the custom project id
  * @param projectRunConfigurationId integer representing the project run configuration id
  * @param contractEntityId string representing the servicing provider key
 */
  public async getAccessPointsByContractEntity(projectId: number, projectRunConfigurationId: number, contractEntityId: number): Promise<ServicingAccessPoint[]> {
    return await this.makeGet<ServicingAccessPoint[]>(`${this.endpoint}/projects/${projectId}/run-configurations/${projectRunConfigurationId}/contract-entity/${contractEntityId}/access-points`);
  }

  /**
   * Get the max provider metric for the given project and run configuration
   *  @param projectId integer representing the custom project id
   * @param projectRunConfigurationId integer representing the project run configuration id
   */
  public async getMaxProviderMetric(projectId: number, projectRunConfigurationId: number): Promise<MaxProviderMetric> {
    return await this.makeGet<MaxProviderMetric>(`/ServicingAccessPoints/projects/${projectId}/run-configurations/${projectRunConfigurationId}/max-provider-metric`);
  }

  /**
   * Get the access point details for a given access point
   *  @param accessPointId integer representing the access point id
   *  @param servicingProviderKey string representing the servicing provider key
   *  @param projectId integer representing the custom project id
   *  @param projectRunConfigurationId integer representing the project run configuration id
   */
  public async getAccessPointDetails(servicingAccessPointId: number, projectId: number, projectRunConfigurationId: number): Promise<AccessPointDetails> {
    return await this.makeGet<AccessPointDetails>(`${this.endpoint}/projects/${projectId}/run-configurations/${projectRunConfigurationId}/access-point/${servicingAccessPointId}/county-details`);
  }

  /**
   * Get the provider access point results filtered by access gap type
   *  @param projectId integer representing the custom project id
   *  @param projectRunConfigurationId integer representing the project run configuration id
   *  @param servicingProviderKey string representing the servicing provider key
   *  @param gapType AccessGapTypeEnum to filter by
   */
  public async getProviderServicingAccessPointsByAccessGapTypeAsync(projectId: number, projectRunConfigurationId: number, servicingProviderKey: string, gapType: AccessGapTypeEnum): Promise<ProviderServicingAccessPoint[]> {
    return await this.makeGet<ProviderServicingAccessPoint[]>(`${this.endpoint}/projects/${projectId}/run-configurations/${projectRunConfigurationId}/servicing-provider/${servicingProviderKey}/servicing-access-points/gap-type/${gapType}`);
  }

  /**
  * Get the provider access point results filtered by access gap type
  *  @param projectId integer representing the custom project id
  *  @param projectRunConfigurationId integer representing the project run configuration id
  *  @param contractEntityId integer representing the contract entity id
  *  @param gapType AccessGapTypeEnum to filter by
  */
  public async getProviderServicingAccessPointsByAccessGapTypeAndContractEntityAsync(projectId: number, projectRunConfigurationId: number, contractEntityId: number, gapType: AccessGapTypeEnum): Promise<ProviderServicingAccessPoint[]> {
    return await this.makeGet<ProviderServicingAccessPoint[]>(`${this.endpoint}/projects/${projectId}/run-configurations/${projectRunConfigurationId}/contract-entity/${contractEntityId}/servicing-access-points/gap-type/${gapType}`);
  }

  public async getProviderServicingAccessPointsByAccessGapTypeAndHospitalAsync(projectId: number, projectRunConfigurationId: number, ccn: string, gapType: AccessGapTypeEnum): Promise<ProviderServicingAccessPoint[]> {
    return await this.makeGet<ProviderServicingAccessPoint[]>(`${this.endpoint}/projects/${projectId}/run-configurations/${projectRunConfigurationId}/hospital/${ccn}/servicing-access-points/gap-type/${gapType}`);
  }

  /**
   * Get the provider access point results filtered by access gap type
   *  @param projectId integer representing the custom project id
   *  @param projectRunConfigurationId integer representing the project run configuration id
   */
  public async getMaxContractEntityMetricAsync(projectId: number, projectRunConfigurationId: number): Promise<MaxContractEntityMetric> {
    return await this.makeGet<MaxContractEntityMetric>(`${this.endpoint}/projects/${projectId}/run-configurations/${projectRunConfigurationId}/max-contract-entity-metric`);
  }

  public async getAccessPointsByOrganization(projectId: number, projectRunConfigurationId: number, organizationNpi: string): Promise<ServicingAccessPoint[]> {
    return await this.makeGet<ServicingAccessPoint[]>(`${this.endpoint}/projects/${projectId}/run-configurations/${projectRunConfigurationId}/organization/${organizationNpi}/access-points`);
  }

  public async getAccessPointsByHospital(projectId: number, projectRunConfigurationId: number, ccn: string, signal?: AbortSignal): Promise<ServicingAccessPoint[]> {
    return await this.makeGet<ServicingAccessPoint[]>(`${this.endpoint}/projects/${projectId}/run-configurations/${projectRunConfigurationId}/hospital/${ccn}/access-points`, undefined, undefined, false, undefined, signal);
  }

  public async getProviderHubOrganizations(
    pageIndex: number,
    pageSize: number,
    filters: ProviderHubOrganizationsFiltersRequestModel
  ): Promise<GapImpactPaginationResult<ProviderHubOrganizations>> {
    const data = await this.makePost<GapImpactPaginationResult<ProviderHubOrganizations>>(
      `${this.endpoint}/projects/${filters.projectId}/run-configurations/${filters.projectRunConfigurationId}/organizations`,
      { ...filters, pageIndex: --pageIndex, pageSize }
    );
    return data;
  }

  public async getProviderHubHospitals(
    pageIndex: number,
    pageSize: number,
    filters: ProviderHubHospitalsFiltersRequestModel
  ): Promise<GapImpactPaginationResult<ProviderHubHospitals>> {
    const data = await this.makePost<GapImpactPaginationResult<ProviderHubHospitals>>(
      `${this.endpoint}/projects/${filters.projectId}/run-configurations/${filters.projectRunConfigurationId}/hospitals`,
      { ...filters, pageIndex: --pageIndex, pageSize }
    );
    return data;
  }

  public async resetIngestion(projectId: number, projectRunConfigurationIds: number[]): Promise<void> {
    await this.makePost<void>(`${this.endpoint}/projects/${projectId}/reset-ingestion`, { projectRunConfigurationIds });
  }

  public async getMarketProviderJobStatus(projectId: number, projectRunConfigurationId: number): Promise<MarketProviderJobStatusResponse> {
    return await this.makeGet<MarketProviderJobStatusResponse>(`networkintegrity/projects/${projectId}/project-run-configurations/${projectRunConfigurationId}/market-provider-job-status`)
  }

  /** Get all filterable Top Procedures for servicing providers in the given project run. */
  public async getTopProcedures(projectId: number, projectRunConfigurationId: number, signal: AbortSignal): Promise<Procedure[]> {
    return await this.makeGet<Procedure[]>(
      `${this.endpoint}/projects/${projectId}/run-configurations/${projectRunConfigurationId}/servicing-providers/procedure-codes`,
      undefined,
      undefined,
      false,
      undefined,
      signal
    );
  }

  /** Get all filterable Top Conditions for servicing providers in the given project run. */
  public async getTopConditions(projectId: number, projectRunConfigurationId: number, signal: AbortSignal): Promise<Condition[]> {
    return await this.makeGet<Condition[]>(
      `${this.endpoint}/projects/${projectId}/run-configurations/${projectRunConfigurationId}/servicing-providers/condition-codes`,
      undefined,
      undefined,
      false,
      undefined,
      signal
    );
  }

  /** Get all filterable Top Prescriptions for servicing providers in the given project run. */
  public async getTopPrescriptions(projectId: number, projectRunConfigurationId: number, signal: AbortSignal): Promise<string[]> {
    return await this.makeGet<string[]>(
      `${this.endpoint}/projects/${projectId}/run-configurations/${projectRunConfigurationId}/servicing-providers/prescriptions`,
      undefined,
      undefined,
      false,
      undefined,
      signal
    );
  }

  /** Get data used for PCI Dashboard's Ready for Review widgets */
  public async getPciDashboardReadyForReviewData(filters: ProviderHubFiltersRequestModel, signal: AbortSignal): Promise<ReadyForReviewModel> {
    return await this.makePost<ReadyForReviewModel>(
      `${this.endpoint}/servicing-providers/ready-for-review`,
      filters,
      undefined,
      false,
      "application/json",
      true,
      undefined,
      signal
    );
  }

  /** Get data used for PCI Dashboard's Provider Status Breakdown table */
  public async getPciDashboardClaimsVolumeBreakdownData(filters: ProviderHubFiltersRequestModel, signal: AbortSignal): Promise<ProviderCountPerClaimsVolumePerLOB[]> {
    return await this.makePost<ProviderCountPerClaimsVolumePerLOB[]>(
      `${this.endpoint}/servicing-providers/claims-volume-breakdown`,
      filters,
      undefined,
      false,
      "application/json",
      true,
      undefined,
      signal
    );
  }

  /** Get data used for PCI Dashboard's Effectiveness and Efficiency graphs */
  public async getPciDashboardEffectivenessEfficiencyData(filters: ProviderHubFiltersRequestModel, signal: AbortSignal): Promise<EffectivenessEfficiencyBreakdown> {
    return await this.makePost<EffectivenessEfficiencyBreakdown>(
      `${this.endpoint}/servicing-providers/effectiveness-efficiency-breakdown`,
      filters,
      undefined,
      false,
      "application/json",
      true,
      undefined,
      signal
    );
  }
}